"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import RawMaterialsListPrint from "@/components/print/RawMaterialsListPrint";
import Button from "@/components/ui/button/Button";
import DataTable from "@/components/ui/DataTable";
import DropdownSelect from "@/components/ui/DropdownSelect";
import DrawerPanel from "@/components/ui/DrawerPanel";
import Input from "@/components/form/input/InputField";
import Radio from "@/components/form/input/Radio";
import Label from "@/components/form/Label";
import { Modal } from "@/components/ui/modal/index";
import { PencilIcon, TrashBinIcon } from "@/icons";
import { handleApiError } from "@/lib/api";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import toast from "react-hot-toast";

const UOMS = ["TON", "KG", "BAGS", "PIECE", "CUM", "LITRE", "LTR"];

const emptyForm = () => ({ name: "", uom: "TON", grade: "", wastage: "", is_active: true });

function formatWastage(value) {
  if (value == null || value === "") return "—";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? `${parsed.toFixed(2)}%` : `${value}%`;
}

function uomSelectOptions(currentUom) {
  if (currentUom && !UOMS.includes(currentUom)) return [...UOMS, currentUom];
  return UOMS;
}

export default function RmcPurchaseMaterials() {
  const qc = useQueryClient();
  const printRef = React.useRef(null);
  const [pdfBusy, setPdfBusy] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [deleteRow, setDeleteRow] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ["materials"],
    queryFn: async () => (await rmcClient.listMaterials()).data,
  });

  const closeDrawer = () => {
    setOpen(false);
    setEditingId(null);
    setForm(emptyForm());
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name ?? "",
      uom: row.uom ?? "TON",
      grade: row.grade ?? row.grade_text ?? "",
      wastage: row.wastage ?? "",
      is_active: row.is_active !== false,
    });
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name.trim(),
        uom: form.uom,
        grade: form.grade.trim() || "",
        wastage: form.wastage === "" ? "" : String(form.wastage),
        is_active: Boolean(form.is_active),
      };
      if (editingId != null) {
        return rmcClient.updateMaterial(editingId, payload);
      }
      return rmcClient.createMaterial(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["materials"] });
      toast.success(editingId != null ? "Material updated" : "Material added");
      closeDrawer();
    },
    onError: (e) => toast.error(handleApiError(e)),
  });

  const remove = useMutation({
    mutationFn: (id) => rmcClient.deleteMaterial(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["materials"] });
      toast.success("Material deleted");
      setDeleteRow(null);
    },
    onError: (e) => toast.error(handleApiError(e)),
  });

  const exportRawMaterialsPdf = async () => {
    setPdfBusy(true);
    try {
      await printRef.current?.openPrint?.();
    } catch {
      toast.error("Could not prepare PDF export");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <RawMaterialsListPrint ref={printRef} />
      <PageBreadCrumb pageTitle="Raw Material Master" />
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={pdfBusy || isFetching}
          onClick={exportRawMaterialsPdf}
        >
          {pdfBusy ? "Preparing…" : "Export PDF"}
        </Button>
        <Button size="sm" onClick={openCreate}>
          Add
        </Button>
      </div>
      <DataTable
        columns={[
          { key: "name", header: "Name" },
          { key: "uom", header: "UOM" },
          {
            key: "grade",
            header: "Grade",
            render: (r) => r.grade ?? r.grade_text ?? "—",
          },
          {
            key: "wastage",
            header: "Wastage %",
            render: (r) => formatWastage(r.wastage),
          },
          {
            key: "is_active",
            header: "Status",
            render: (r) => (r.is_active !== false ? "Active" : "Inactive"),
          },
          {
            key: "last_rate",
            header: "Last rate",
            render: (r) => (r.last_rate ? `₹${Number(r.last_rate).toFixed(2)}` : "—"),
          },
          {
            key: "actions",
            header: "",
            headerClassName: "text-right",
            cellClassName: "text-right",
            render: (row) => (
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  aria-label={`Edit ${row.name ?? "material"}`}
                  title="Edit"
                  disabled={save.isPending || remove.isPending}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(row);
                  }}
                >
                  <PencilIcon className="size-4" />
                </button>
                <button
                  type="button"
                  aria-label={`Delete ${row.name ?? "material"}`}
                  title="Delete"
                  disabled={save.isPending || remove.isPending}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-red-50 hover:text-red-600 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-red-500/10 dark:hover:text-red-400 disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteRow(row);
                  }}
                >
                  <TrashBinIcon className="size-4" />
                </button>
              </div>
            ),
          },
        ]}
        data={rows}
        isLoading={isFetching}
      />
      <DrawerPanel
        open={open}
        onClose={() => {
          if (!save.isPending) closeDrawer();
        }}
        title={editingId != null ? "Edit material" : "Add material"}
      >
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <DropdownSelect
              label="UOM"
              value={form.uom}
              onChange={(uom) => setForm({ ...form, uom })}
              options={uomSelectOptions(form.uom).map((u) => ({ value: u, label: u }))}
            />
          </div>
          <div>
            <Label>Grade (optional)</Label>
            <Input
              value={form.grade}
              onChange={(e) => setForm({ ...form, grade: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Wastage %</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.wastage}
              onChange={(e) => setForm({ ...form, wastage: e.target.value })}
              className="mt-1"
              placeholder="0.00"
            />
          </div>
          <div>
            <Label>Status</Label>
            <div className="mt-2 flex items-center gap-6">
              <Radio
                id="material-status-active"
                name="material-status"
                value="active"
                label="Active"
                checked={form.is_active}
                onChange={() => setForm({ ...form, is_active: true })}
              />
              <Radio
                id="material-status-inactive"
                name="material-status"
                value="inactive"
                label="Inactive"
                checked={!form.is_active}
                onChange={() => setForm({ ...form, is_active: false })}
              />
            </div>
          </div>
          <Button
            size="sm"
            disabled={!form.name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Saving..." : editingId != null ? "Update" : "Save"}
          </Button>
        </div>
      </DrawerPanel>

      <Modal
        isOpen={deleteRow != null}
        onClose={() => {
          if (!remove.isPending) setDeleteRow(null);
        }}
        className="max-w-md w-full"
      >
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Delete material?</h3>
          <p className="text-sm text-gray-400">
            This will permanently remove{" "}
            <span className="font-medium text-gray-200">{deleteRow?.name ?? "this material"}</span>
            .
          </p>
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={remove.isPending}
              onClick={() => setDeleteRow(null)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={remove.isPending}
              onClick={() => deleteRow?.id && remove.mutate(deleteRow.id)}
            >
              {remove.isPending ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
